import { createRouter, createWebHistory } from 'vue-router';
import { auth } from '../firebase';
import Home from '../components/Home.vue';
import AdminDashboard from '../components/AdminDashboard.vue';
import Login from '../components/Login.vue';
import MemberArea from '../components/MemberArea.vue';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';

// Returns a promise that resolves with the current user once Firebase auth is ready
function getCurrentUser() {
  return new Promise((resolve) => {
    if (!auth) return resolve(null);
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });
}

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home
  },
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: AdminDashboard,
    meta: { requiresAuth: true }
  },
  {
    path: '/member',
    name: 'MemberArea',
    component: MemberArea,
    meta: { requiresAuth: true }
  },
  {
    path: '/login',
    name: 'Login',
    component: Login
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

// Navigation guard to protect admin routes
router.beforeEach(async (to, _from, next) => {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  
  if (requiresAuth) {
    // Check if Firebase is properly initialized
    if (!auth || !db) {
      console.warn('Firebase not configured. Please update src/firebase.js with your Firebase configuration.');
      return next({ path: '/login' });
    }

    try {
      // Wait for Firebase to restore auth state before checking
      const currentUser = await getCurrentUser();

      if (!currentUser) {
        return next({ path: '/login' });
      }
      
      // Check if user is approved
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.approved || userData.role === 'admin') {
          // Redirect /admin to /member unless the user is explicitly an admin
          if (to.path === '/admin' && userData.role !== 'admin') {
            return next({ path: '/member' });
          }
          next();
        } else {
          next({ path: '/login', query: { pending: 'true' } });
        }
      } else {
        // New user (e.g. first Google sign-in) — create their doc and send to pending
        await setDoc(doc(db, 'users', currentUser.uid), {
          email: currentUser.email,
          approved: false,
          role: 'user',
          createdAt: new Date()
        });
        next({ path: '/login', query: { pending: 'true' } });
      }
    } catch (error) {
      console.error('Error checking user auth:', error);
      // If Firebase isn't configured or there's an error, redirect to login
      if (error.code === 'app/invalid-credential' || 
          error.code === 'auth/invalid-api-key' ||
          error.message?.includes('invalid') || 
          error.message?.includes('API key') ||
          error.message?.includes('The requested action is invalid')) {
        console.warn('Firebase configuration error. Please configure Firebase in src/firebase.js');
      }
      next({ path: '/login' });
    }
  } else {
    next();
  }
});

export default router;
